import { chunkRecovery } from '~/lib/chunkRecovery'
import { chunkRecoveryLatchSchema } from '~/lib/chunkRecoveryLatchSchema'
import type { ChunkRecoveryLatch } from '~/lib/ChunkRecoveryLatch'

const {
  CHUNK_RECOVERY_BOOT_LATCH_KEY,
  CHUNK_RECOVERY_LATCH_KEY,
  CHUNK_RECOVERY_MAX_ATTEMPTS,
  isRecoverableChunkError,
  nextChunkRecoveryLatch
} = chunkRecovery

// In-memory guard: collapses every trigger within one page life into a single reload.
let recoveryInFlight = false

export default defineNuxtPlugin((nuxtApp) => {
  const logger = useLogger('ChunkRecovery')

  const readLatch = (): ChunkRecoveryLatch | null => {
    try {
      const raw = sessionStorage.getItem(CHUNK_RECOVERY_LATCH_KEY)
      if (raw === null) {
        return null
      }
      const parsed = chunkRecoveryLatchSchema.safeParse(JSON.parse(raw))
      return parsed.success ? parsed.data : null
    } catch (error) {
      logger.warn('readLatch', { error })
      return null
    }
  }

  const recordAttempt = (now: number): number => {
    const next = nextChunkRecoveryLatch(readLatch(), now)
    try {
      sessionStorage.setItem(CHUNK_RECOVERY_LATCH_KEY, JSON.stringify(next))
    } catch (error) {
      logger.warn('recordAttempt', { error })
    }
    return next.count
  }

  const clearLatch = (): void => {
    try {
      sessionStorage.removeItem(CHUNK_RECOVERY_LATCH_KEY)
      sessionStorage.removeItem(CHUNK_RECOVERY_BOOT_LATCH_KEY)
    } catch (error) {
      logger.warn('clearLatch', { error })
    }
  }

  nuxtApp.hook('app:error', (error: unknown) => {
    if (!isRecoverableChunkError(error)) {
      return
    }

    // Vite surfaces the real cause in dev; reloading would just hide it.
    if (import.meta.dev) {
      logger.warn('skipRecoveryInDev', { error })
      return
    }

    if (recoveryInFlight) {
      logger.debug('recoveryAlreadyInFlight', {})
      return
    }
    recoveryInFlight = true

    const attempt = recordAttempt(Date.now())
    if (attempt > CHUNK_RECOVERY_MAX_ATTEMPTS) {
      logger.error('recoveryExhausted', { attempt, maxAttempts: CHUNK_RECOVERY_MAX_ATTEMPTS })
      return
    }

    logger.warn('reloadingAfterChunkError', { attempt, path: window.location.pathname })
    window.location.reload()
  })

  // A completed navigation proves chunk loading works, so the next failure starts fresh.
  useRouter().afterEach(() => {
    clearLatch()
  })
})
